import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as stylex from '@stylexjs/stylex';
import { clsx } from 'clsx';
import { forwardRef, isValidElement, useEffect, useState } from 'react';
import Button from '../Button';
import Icon from '../Icon';
import TextField from '../TextField';
import { colors } from '../theme/colors.stylex';
import type { StyleXArray, StyleXStyles, Theme } from '@stylexjs/stylex';
import type { ModalProps } from './Modal.types';

// oxlint-disable-next-line no-undef
const CSS_TRANSITION_DURATION = process.env.NODE_ENV === 'test' ? 0 : 300; // Disable animations during testing

const styles = stylex.create({
  portal: { position: 'fixed', inset: 0, zIndex: 1010, overflowY: 'auto' },
  backdrop: {
    position: 'absolute',
    inset: 0,
    height: '100%',
    width: '100%',
    backgroundColor: colors.modalBackdrop,
    opacity: 0.5,
    userSelect: 'none',
  },
  dialog: {
    boxSizing: 'border-box',
    position: 'fixed',
    top: 0,
    left: '50%',
    zIndex: 1,
    marginBlock: '1.5rem',
    width: '100%',
    transform: 'translateX(-50%)',
    gap: 0,
    borderRadius: '0.5rem',
    borderWidth: 0,
    backgroundColor: colors.background,
    color: colors.text,
    maxHeight: 'calc(100% - 3.5rem)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: `#00000080 0 5px 15px, inset 0 0 0 1px ${colors.modalBorder}`,
  },
  duration: (duration: number) => ({ transitionDuration: `${duration}ms` }),
  hidden: { display: 'none' },
  headerWrapper: { width: '100%', justifyContent: 'space-between' },
  header: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    borderWidth: 0,
    borderBottomWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.border,
    paddingInline: '1.5rem',
    paddingBlock: '1rem',
  },
  row: { display: 'flex', width: '100%' },
  center: { display: 'flex', alignItems: 'center' },
  editable: { display: 'flex', width: '100%', alignItems: 'center' },
  customIcon: { marginTop: '0.5rem' },
  icon: { display: 'flex', fontSize: '1.25rem', marginTop: '0.25rem', lineHeight: 1.5 },
  leftIcon: { marginRight: '0.5rem', display: 'flex' },
  rightIcon: { marginLeft: '1rem', display: 'flex' },
  error: { marginBottom: 0, minWidth: 'fit-content', paddingLeft: '0.5rem', color: colors.borderInvalid },
  suffix: { paddingLeft: '0.125rem', fontSize: '1.25rem', lineHeight: '1.75rem' },
  subtitle: {
    marginTop: '0.25rem',
    textAlign: 'left',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    fontStyle: 'italic',
  },
  close: {
    fontFamily: 'inherit',
    color: 'inherit',
    padding: 0,
    borderWidth: 0,
    marginLeft: '1rem',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    fontSize: '1rem',
    lineHeight: '1.5rem',
    opacity: { 'default': 0.7, ':hover': 1 },
    fontWeight: 700,
    outlineStyle: { 'default': null, ':focus-visible': 'none' },
  },
  pointer: { cursor: 'pointer' },
  body: { flex: 1, overflowY: 'auto', paddingInline: '1.5rem', paddingBlock: '1rem' },
  footer: { paddingInline: '1.5rem', paddingBlock: '1rem' },
  footerRow: { display: 'flex', width: '100%', justifyContent: 'space-between' },
  start: { display: 'flex', justifyContent: 'flex-start' },
  end: { display: 'flex', justifyContent: 'flex-end', columnGap: '0.5rem' },
  customButton: { justifyContent: 'flex-start', minWidth: '6.25rem' },
  cancelButton: { minWidth: '6.25rem' },
  submitButton: { minWidth: '6.25rem' },
});
const sizes = stylex.create({
  'xs': { maxWidth: '20rem' },
  'sm': { maxWidth: '24rem' },
  'md': { maxWidth: '28rem' },
  'lg': { maxWidth: '32rem' },
  'xl': { maxWidth: '36rem' },
  '2xl': { maxWidth: '42rem' },
  '3xl': { maxWidth: '48rem' },
  '4xl': { maxWidth: '56rem' },
  '5xl': { maxWidth: '64rem' },
  '6xl': { maxWidth: '72rem' },
});

const Dialog = DialogPrimitive.Root;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogContent = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    xstyle?: StyleXArray<StyleXStyles | Theme<typeof colors>>;
  }
>(({ className = '', 'aria-describedby': ariaDescribedBy, children, xstyle, ...props }, ref) => {
  const dialogProps = stylex.props(styles.dialog, styles.duration(CSS_TRANSITION_DURATION), xstyle);
  return (
    <DialogPortal>
      {/* We will not use DialogPrimitive.Overlay because it is not triggered if we have modal prop set to false, and
     we need to have modal prop set to false, because if not, then if we will have a popover with inputs then the
     focus will be automatically trapped */}
      <div {...stylex.props(styles.portal)}>
        <div {...stylex.props(styles.backdrop)} />
        <DialogPrimitive.Content
          ref={ref}
          // Radix FocusScope makes Dialog.Content programmatically focusable with tabIndex=-1. Removing the DOM
          // attribute keeps portaled controls from handing Tab focus back to the dialog content itself.
          tabIndex={undefined}
          aria-describedby={ariaDescribedBy}
          {...dialogProps}
          className={clsx(dialogProps.className, className)}
          {...props}>
          {/*
         Due to some slight changes in ref handling in react 19, `DialogContent` does always contain a `DialogTitle`,
         but some forward ref logic we use here seems to cause the title to not get detected correctly. This doesn't
         impact functionality, but it does spout errors in any unit tests that use the modal, which cause failures.
         This line below is a no-impact way to skip out on the errors.
         */}
          <DialogPrimitive.DialogTitle {...stylex.props(styles.hidden)}></DialogPrimitive.DialogTitle>
          {children}
        </DialogPrimitive.Content>
      </div>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...stylex.props(styles.headerWrapper)}>
    <div {...props} />
  </div>
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = (props: React.HTMLAttributes<HTMLDivElement>) => <div {...props} />;
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>((props, ref) => <DialogPrimitive.Title ref={ref} {...props} />);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>((props, ref) => (
  <DialogPrimitive.Description asChild>
    <div ref={ref} {...props} />
  </DialogPrimitive.Description>
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const Modal = ({
  titleIcon,
  title,
  titleSuffixLabel,
  subtitle,
  children,
  open = false,
  onClose,
  onCloseAutoFocus,
  customButton = false,
  customButtonLabel = 'Back',
  onClickCustomButton,
  submitButtonLabel = 'Submit',
  cancelButtonLabel = 'Cancel',
  disableSubmitButton = false,
  stopPropagationSubmitButton = false,
  onSubmit,
  isTitleEditable = false,
  onTitleChanged,
  inputExtraClassNames,
  hideCloseIcon = false,
  size = 'xl',
  titleIconPosition = 'left',
  hideFooterButtons = false,
  hideSubmitButton = false,
  hideCancelButton = false,
  testId = 'modal',
  modalFooter,
  dialogClassName,
  xstyle,
  titlePlaceholder,
  titleRequired,
  titleError,
  submitButtonTooltip,
  cancelButtonTooltip,
  disableCustomButton,
  customHeader,
  middleFooterSection = <></>,
  customButtonVariant = 'outline',
  submitButtonVariant = 'theme',
  keepFocusInsideModal = true,
  onPointerDownOutside,
  onInteractOutside,
  ariaDescribedBy,
}: ModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  // the Dialog is adding pointerEvents: none to body and the dropdowns from the modal does not work anymore
  useEffect(() => {
    if (open) {
      // Pushing the change to the end of the call stack
      const timer = setTimeout(() => {
        document.body.style.pointerEvents = '';
      }, 0);

      return () => clearTimeout(timer);
    } else {
      document.body.style.pointerEvents = 'auto';
    }
  }, [open]);

  const handleSubmit = async (e: React.MouseEvent) => {
    if (!onSubmit) return;
    try {
      setIsLoading(true);
      await onSubmit(e);
    } finally {
      setIsLoading(false);
    }
  };

  const renderTitle = () => {
    let titleIconElement = <></>;

    if (titleIcon) {
      if (isValidElement(titleIcon)) {
        titleIconElement = <div {...stylex.props(styles.customIcon)}>{titleIcon}</div>;
      } else {
        titleIconElement = <Icon icon={titleIcon} testId="modalTitleIcon" xstyle={styles.icon} />;
      }
    }

    return (
      <>
        {titleIcon && titleIconPosition === 'left' ? (
          <div {...stylex.props(styles.leftIcon)}>{titleIconElement}</div>
        ) : (
          <></>
        )}
        <DialogTitle asChild>
          {isTitleEditable ? (
            <div {...stylex.props(styles.editable)}>
              <TextField
                extraClassNames={inputExtraClassNames}
                value={title}
                type="text"
                testId="modalTitle"
                onChange={onTitleChanged}
                placeholder={titlePlaceholder}
                required={titleRequired}
                showError={!!titleError}
              />
              {titleError && <p {...stylex.props(styles.error)}>{titleError}</p>}
            </div>
          ) : (
            (customHeader ?? (
              <div data-testid="modalTitle" className="modal-title">
                <div {...stylex.props(styles.center)}>
                  <h3>{title}</h3>
                  {titleSuffixLabel && <span {...stylex.props(styles.suffix)}>{titleSuffixLabel}</span>}
                </div>
                {subtitle && (
                  <div {...stylex.props(styles.subtitle)} data-testid="modal-subtitle">
                    {subtitle}
                  </div>
                )}
              </div>
            ))
          )}
        </DialogTitle>
        {titleIcon && titleIconPosition === 'right' ? (
          <div {...stylex.props(styles.rightIcon)}>{titleIconElement}</div>
        ) : (
          <></>
        )}
      </>
    );
  };

  const headerProps = stylex.props(styles.header);
  const closeProps = stylex.props(styles.close);
  const bodyProps = stylex.props(styles.body);
  const footerProps = stylex.props(styles.footer);
  return open ? (
    <Dialog open={true} onOpenChange={onClose} modal={keepFocusInsideModal}>
      <DialogContent
        onCloseAutoFocus={onCloseAutoFocus}
        onPointerDownOutside={(e) => (onPointerDownOutside ? onPointerDownOutside(e) : e.preventDefault())}
        onInteractOutside={(e) => (onInteractOutside ? onInteractOutside(e) : e.preventDefault())}
        data-testid={testId}
        aria-describedby={ariaDescribedBy}
        xstyle={[sizes[size], xstyle]}
        className={clsx('modalContent', dialogClassName)}>
        <DialogHeader {...headerProps} className={clsx(headerProps.className, 'modal-header')}>
          <div {...stylex.props(styles.row)}>{renderTitle()}</div>
          {!hideCloseIcon && (
            <DialogClose
              autoFocus={false}
              {...closeProps}
              className={clsx(closeProps.className, 'close')}
              data-testid="closeButton">
              <span {...stylex.props(styles.pointer)}>&times;</span>
            </DialogClose>
          )}
        </DialogHeader>
        <DialogDescription {...bodyProps} className={clsx(bodyProps.className, 'modal-body')}>
          {children}
        </DialogDescription>
        {!hideFooterButtons && (
          <DialogFooter {...footerProps} className={clsx(footerProps.className, 'modal-footer')}>
            {modalFooter ?? (
              <div {...stylex.props(styles.footerRow)} data-testid="modalFooter">
                <div {...stylex.props(styles.start)}>
                  {customButton && (
                    <Button
                      label={customButtonLabel}
                      onClick={onClickCustomButton}
                      disabled={disableCustomButton}
                      xstyle={styles.customButton}
                      testId="customButton"
                      variant={customButtonVariant}
                    />
                  )}
                </div>
                <div {...stylex.props(styles.end)}>
                  <div {...stylex.props(styles.center)}>{middleFooterSection}</div>
                  {!hideCancelButton && (
                    <DialogClose asChild>
                      <Button
                        label={cancelButtonLabel}
                        xstyle={styles.cancelButton}
                        tooltip={cancelButtonTooltip}
                        variant="outline"
                        stopPropagation={false}
                        testId="cancelButton"
                      />
                    </DialogClose>
                  )}
                  {!hideSubmitButton && (
                    <Button
                      label={submitButtonLabel}
                      onClick={handleSubmit}
                      disabled={disableSubmitButton || isLoading}
                      variant={submitButtonVariant}
                      stopPropagation={stopPropagationSubmitButton}
                      tooltip={submitButtonTooltip}
                      inProgress={isLoading}
                      iconPosition="left"
                      testId="submitButton"
                      xstyle={styles.submitButton}
                    />
                  )}
                </div>
              </div>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  ) : (
    <></>
  );
};

export default Modal;
